<template>
  <div class="mt-8" v-if="modelValue">
    <h4 class="text-base mb-11">
      {{ wt('structure', 'foundingYear', 'title') }}
    </h4>

    <section class="boxed-button-grid mb-12">
      <label
        v-for="option in FoundingYear"
        :key="option"
        class="base-button"
        :class="{
          active: modelValue?.foundingYear === option,
        }"
        >{{ wt('structure', 'foundingYear', 'option', option) }}
        <input type="radio" :value="option" v-model="modelValue.foundingYear" :id="option" name="foundingYear" />
      </label>
    </section>

    <h4 class="text-base mb-11">
      {{ wt('structure', 'officeSize', 'title') }}
    </h4>
    <div class="mb-6 grid lg:grid-cols-6-2">
      <section class="mb-12">
        <div class="slider-wrapper relative">
          <div class="m-auto w-full lg:w-1/2">
            <component :is="getOfficeSizeIcon" />
          </div>

          <RangeInput
            @input="getOfficeSize($event.target.value)"
            v-model="officeSizeIndex"
            colorVariant="primary"
            :min="0"
            :max="5"
            :step="1"
            class="mt-6" />
          <span class="slider-value absolute text-neutral-550 lg:bottom-0">
            {{ wt('structure', 'officeSize', 'option', modelValue.officeSize) }}
            {{ wt('structure', 'officeSize', 'employees') }}
          </span>
        </div>
      </section>
    </div>

    <h4 class="text-base mb-11">
      {{ wt('structure', 'internalRoles', 'title') }}
    </h4>
    <section class="boxed-button-grid mb-12">
      <label
        v-for="option in InternalRole"
        :key="option"
        class="base-button text-center"
        :class="{
          active: hasInternalRoles(option),
        }"
        >{{ wt('structure', 'internalRoles', 'option', option) }}
        <input type="checkbox" :value="option" v-model="modelValue.internalRoles" :id="option" name="internalRoles" />
      </label>
    </section>
  </div>
</template>
<script setup lang="ts">
import { FoundingYear, InternalRole, OfficeSize } from '@/schemas/general/enums'
import { computed, onMounted, type PropType, ref } from 'vue'
import RangeInput from '@/components/partials/inputs/RangeInput.vue'
import { useWidgetText } from '@/helpers/useWidgetText'
import * as Icon_ONE_TWO from '@/assets/icons/office-size/1-2.svg'
import * as Icon_THREE_EIGHT from '@/assets/icons/office-size/3-8.svg'
import * as Icon_NINE_SEVENTEEN from '@/assets/icons/office-size/9-15.svg'
import * as Icon_EIGHTEEN_THIRTY from '@/assets/icons/office-size/16-30.svg'
import * as Icon_THIRTY_ONE_FIFTY from '@/assets/icons/office-size/31-51.svg'
import * as Icon_MORE_THAN_FIFTY from '@/assets/icons/office-size/51plus.svg'

interface OfficeStructureForm {
  foundingYear: FoundingYear
  officeSize: OfficeSize
  internalRoles: InternalRole[]
}

const officeSizeIcons = [
  Icon_ONE_TWO,
  Icon_THREE_EIGHT,
  Icon_NINE_SEVENTEEN,
  Icon_EIGHTEEN_THIRTY,
  Icon_THIRTY_ONE_FIFTY,
  Icon_MORE_THAN_FIFTY,
]

const getOfficeSizeIcon = computed(() => {
  return officeSizeIcons[officeSizeIndex.value]
})

const { wt } = useWidgetText('widget-texts-office')
const modelValue = defineModel({ type: Object as PropType<OfficeStructureForm> })
const officeSizeIndex = ref(0)

onMounted(() => {
  if (modelValue.value?.officeSize) {
    officeSizeIndex.value = Object.keys(OfficeSize).indexOf(modelValue.value?.officeSize)
  }
})

const hasInternalRoles = function (option: InternalRole): boolean | undefined {
  return modelValue.value?.internalRoles?.includes(option)
}

const getOfficeSize = function (value: number) {
  if (modelValue.value?.officeSize) {
    modelValue.value.officeSize = Object.values(OfficeSize)[value]
  }
}
</script>
